import { useContext, useEffect, useState } from "react";
import { ReactReduxContext } from "react-redux";
import PropTypes from "prop-types";
import { PopupModal } from "../../UI/Modal/Modal";
import CustomToast from "../../UI/customToast";
import { DefaultLanguage } from "../../../constants/Constants";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";

const ViewContactModal = ({ isOpen, contact, onClose, onEdit, loading }) => {
  const reduxContext = useContext(ReactReduxContext);
  const langId = reduxContext?.store?.getState()?.language?.selectedlanguage?.id;
  const [localisedValues, setLocalisedValues] = useState(Localize[langId] || Localize[DefaultLanguage]);
  const ariaLocalised = AriaLocalize[langId] || AriaLocalize['en_US'];

  useEffect(() => {
    if (langId) {
      setLocalisedValues(Localize[langId] || Localize[DefaultLanguage]);
    }
  }, [langId]);

  if (!isOpen) return null;

  if (loading || !contact) {
    const loadingHeader = (
      <div className="modal-title-box">
        <h2 className="modal-title">{localisedValues['contact_details']}</h2>
      </div>
    );
    const loadingBody = (
      <div className="view-contact-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{ariaLocalised.loading}</span>
        </div>
      </div>
    );
    return (
      <PopupModal
        modalClass="view-contact-modal"
        dialogClass="modal-dialog-centered"
        headerContent={loadingHeader}
        bodyContent={loadingBody}
        footerContent={null}
        closeModal={onClose}
        cancelButton={false}
      />
    );
  }

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format phone with dial code
  const formatPhone = () => {
    if (!contact.phone) return localisedValues['not_available'];
    if (contact.phone.startsWith("+")) return contact.phone;
    const dialCode = contact.dialCode ? `+${contact.dialCode}` : "";
    return `${dialCode} ${contact.phone}`.trim();
  };

  // Copy to clipboard handler
  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    CustomToast.success(localisedValues['label_copied'].replace('{label}', label));
  };

  // Header content
  const headerContent = (
    <div className="modal-title-box">
      <h2 className="modal-title">{localisedValues['contact_details']}</h2>
    </div>
  );

  // Body content
  const bodyContent = (
    <div className="view-contact-content">
      {/* Contact Header with Avatar */}
      <div className="view-contact-header">
        <div className="view-contact-avatar" aria-hidden="true">
          {getInitials(contact.name)}
        </div>
        <div className="view-contact-name-section">
          <h3 className="view-contact-name">
            <span className="name-text custom-scroll-div-thin">{contact.name}</span>
            {contact.isFavorite && (
              <span className="favorite-icon active" role="img" aria-label={ariaLocalised.favorite}>
                <span className="for-icon icon-fill-star" aria-hidden="true"></span>
              </span>
            )}
          </h3>
          {contact.company && (
            <p className="view-contact-company custom-scroll-div-thin">{contact.company}</p>
          )}
        </div>
      </div>

      {/* Contact Details List */}
      <div className="view-contact-details">
        {/* Phone */}
        <div className="detail-row">
          <div className="detail-icon" aria-hidden="true">
            <span className="icon-call-fill"></span>
          </div>
          <div className="detail-content">
            <div className="detail-label">{localisedValues['phone']}</div>
            <div className="detail-value">{formatPhone()}</div>
          </div>
          {contact.phone && (
            <button
              type="button"
              className="btn-copy"
              onClick={() => handleCopy(formatPhone(), localisedValues['phone'])}
              title={ariaLocalised.copy_label.replace('{label}', localisedValues['phone'])}
              aria-label={ariaLocalised.copy_label.replace('{label}', localisedValues['phone'])}
            >
              <span className="icon-copy" aria-hidden="true"></span>
            </button>
          )}
        </div>

        {/* Email */}
        <div className="detail-row">
          <div className="detail-icon" aria-hidden="true">
            <span className="icon-email"></span>
          </div>
          <div className="detail-content">
            <div className="detail-label">{localisedValues['email']}</div>
            <div className="detail-value">{contact.email || localisedValues['not_available']}</div>
          </div>
          {contact.email && (
            <button
              type="button"
              className="btn-copy"
              onClick={() => handleCopy(contact.email, localisedValues['email'])}
              title={ariaLocalised.copy_label.replace('{label}', localisedValues['email'])}
              aria-label={ariaLocalised.copy_label.replace('{label}', localisedValues['email'])}
            >
              <span className="icon-copy" aria-hidden="true"></span>
            </button>
          )}
        </div>

        {/* Company */}
        <div className="detail-row">
          <div className="detail-icon" aria-hidden="true">
            <span className="icon-billing"></span>
          </div>
          <div className="detail-content">
            <div className="detail-label">{localisedValues['company']}</div>
            <div className="detail-value company-value custom-scroll-div-thin">{contact.company || localisedValues['not_available']}</div>
          </div>
          {contact.company && (
            <button
              type="button"
              className="btn-copy"
              onClick={() => handleCopy(contact.company, localisedValues['company'])}
              title={ariaLocalised.copy_label.replace('{label}', localisedValues['company'])}
              aria-label={ariaLocalised.copy_label.replace('{label}', localisedValues['company'])}
            >
              <span className="icon-copy" aria-hidden="true"></span>
            </button>
          )}
        </div>

        {/* Notes */}
        <div className="detail-row notes-row">
          <div className="detail-icon" aria-hidden="true">
            <span className="icon-note-created"></span>
          </div>
          <div className="detail-content">
            <div className="detail-label">{localisedValues['notes']}</div>
            <div className="detail-value notes-value custom-scroll-div-thin">{contact.notes || localisedValues['not_available']}</div>
          </div>
          {contact.notes && (
            <button
              type="button"
              className="btn-copy"
              onClick={() => handleCopy(contact.notes, localisedValues['notes'])}
              title={ariaLocalised.copy_label.replace('{label}', localisedValues['notes'])}
              aria-label={ariaLocalised.copy_label.replace('{label}', localisedValues['notes'])}
            >
              <span className="icon-copy" aria-hidden="true"></span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Footer content
  const footerContent = (
    <>
      <div className="button">
        <button
          type="button"
          className="btn btn-outline-secondary font-weight-500"
          onClick={onClose}
        >
          {localisedValues['close']}
        </button>
      </div>
      {onEdit && (
        <div className="button">
          <button
            type="button"
            className="btn btn-primary font-weight-500"
            onClick={() => {
              onClose();
              onEdit(contact);
            }}
          >
            <span className="icon-edit" aria-hidden="true"></span>
            {localisedValues['edit_contact']}
          </button>
        </div>
      )}
    </>
  );

  return (
    <PopupModal
      modalClass="view-contact-modal"
      dialogClass="modal-dialog-centered"
      headerContent={headerContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
      closeModal={onClose}
      cancelButton={false}
    />
  );
};

ViewContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    countryCode: PropTypes.string,
    dialCode: PropTypes.string,
    email: PropTypes.string,
    company: PropTypes.string,
    notes: PropTypes.string,
    isFavorite: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  loading: PropTypes.bool,
};

ViewContactModal.defaultProps = {
  contact: null,
  onEdit: null,
  loading: false,
};

export default ViewContactModal;
